import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { buscarItemsPorNombre, getDataList } from '../api/common';

const baseStyles = {
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const mapItemToOption = (item, disabledSet) => ({
  value: item._id,
  label: item.nombre,
  data: item,
  isDisabled: disabledSet.has(String(item._id)),
});

const ItemSearchSelect = ({
  mode = 'async',
  value = null,
  onChange,
  placeholder = 'Buscar item...',
  isClearable = true,
  menuPortalTarget,
  menuPosition = 'fixed',
  menuPlacement = 'auto',
  styles = {},
  searchMinChars = 3,
  limit = 20,
  disabledIds,
  syncOptions,
  onLoadError,
  noOptionsMessage,
  loadingMessage,
  isDisabled = false,
}) => {
  const [rawSyncItems, setRawSyncItems] = useState([]);
  const [loadingSync, setLoadingSync] = useState(false);

  const disabledSet = useMemo(() => {
    if (disabledIds instanceof Set) return disabledIds;
    return new Set((disabledIds || []).map((id) => String(id)));
  }, [disabledIds]);

  const selectStyles = useMemo(() => ({ ...baseStyles, ...styles }), [styles]);

  useEffect(() => {
    if (mode !== 'sync') return;

    if (Array.isArray(syncOptions)) {
      setRawSyncItems(syncOptions);
      return;
    }

    let isMounted = true;
    const loadSync = async () => {
      setLoadingSync(true);
      try {
        const response = await getDataList('Items');
        const items = response?.data?.data || [];
        if (isMounted) {
          setRawSyncItems(items);
        }
      } catch (error) {
        if (typeof onLoadError === 'function') onLoadError(error);
      } finally {
        if (isMounted) setLoadingSync(false);
      }
    };

    loadSync();
    return () => {
      isMounted = false;
    };
  }, [mode, syncOptions, onLoadError]);

  useEffect(() => {
    if (mode !== 'sync' || !Array.isArray(syncOptions)) return;
    setRawSyncItems(syncOptions);
  }, [mode, syncOptions]);

  const options = useMemo(
    () => rawSyncItems.map((item) => mapItemToOption(item, disabledSet)),
    [rawSyncItems, disabledSet]
  );

  const loadOptions = useCallback(
    async (inputValue) => {
      const term = (inputValue || '').trim();
      if (term.length < searchMinChars) return [];
      try {
        const response = await buscarItemsPorNombre(term, limit);
        const items = response?.data?.data?.items || [];
        return items.map((item) => mapItemToOption(item, disabledSet));
      } catch (error) {
        if (typeof onLoadError === 'function') onLoadError(error);
        return [];
      }
    },
    [searchMinChars, limit, disabledSet, onLoadError]
  );

  const defaultNoOptionsMessage = ({ inputValue }) => {
    if (mode === 'async' && (!inputValue || inputValue.trim().length < searchMinChars)) {
      return `Escriba al menos ${searchMinChars} caracteres...`;
    }
    return 'Sin resultados';
  };

  const defaultLoadingMessage = () => 'Buscando...';

  const sharedProps = {
    value,
    onChange,
    placeholder,
    isClearable,
    menuPortalTarget,
    menuPosition,
    menuPlacement,
    styles: selectStyles,
    noOptionsMessage: noOptionsMessage || defaultNoOptionsMessage,
    loadingMessage: loadingMessage || defaultLoadingMessage,
    isDisabled,
  };

  if (mode === 'sync') {
    return <Select {...sharedProps} options={options} isLoading={loadingSync} isSearchable />;
  }

  return <AsyncSelect {...sharedProps} cacheOptions defaultOptions={false} loadOptions={loadOptions} isSearchable />;
};

export default ItemSearchSelect;
